import { useEffect, useState } from 'react';
import { useUnit } from 'effector-react';
import { useTranslation } from 'react-i18next';
import { $userAccountsStore, $userCardsStore } from '@lib/store';
import { useTabs } from '@shared/hooks';
import { cn, PermissionUtils } from '@shared/utils';
import type { UserResponse } from '@shared/api';
import { RemoveCardsModal } from '../modals';
import { AccountTileSmall, AddCardsModal, Cards, View } from '@widgets';
import { Button, Text } from '@shared/widgets';

interface AccountsCardsProps {
  className?: string;
  user: UserResponse;
}

export const AccountsAndCards = (props: AccountsCardsProps) => {
  const userAccountsStore = useUnit($userAccountsStore.store);
  const userCardsStore = useUnit($userCardsStore.store);

  const [showAddCards, setShowAddCards] = useState<boolean>(false);
  const [showRemoveCards, setShowRemoveCards] = useState<boolean>(false);
  const tabState = useTabs();

  const { t } = useTranslation('user');

  useEffect(() => {
    $userCardsStore.events.getUserCards({ AssignedUserId: props.user.id });
  }, []);

  useEffect(() => {
    tabState.hasPayment && $userAccountsStore.events.getUserAccounts({ AssignedUserId: props.user.id });
    return () => $userAccountsStore.events.clear();
  }, [tabState.hasPayment, props.user.id]);

  const canManageAccounts = PermissionUtils.manageAccountsPermissions();
  const canManageCards = PermissionUtils.manageCardsPermissions();
  return (
    <section className={cn('flex flex-col gap-6', props.className)}>
      <div className='flex flex-wrap items-center justify-between gap-4'>
        <Text weight='medium' size='2xl'>
          {tabState.hasPayment ? `Payment Accounts (${userAccountsStore.data.length})` : `Cards (${userCardsStore.data.data.length})`}
        </Text>
        {(canManageAccounts || canManageCards) && (
          <div className='flex items-center gap-3'>
            <Button color='outlined' onClick={() => setShowAddCards(true)}>
              {t('buttons.add', { ns: 'common' })}
            </Button>
            <Button
              color='outlined'
              disabled={!userAccountsStore.data.length && !userCardsStore.data.data.length}
              onClick={() => setShowRemoveCards(true)}
            >
              {t('buttons.remove', { ns: 'common' })}
            </Button>
          </div>
        )}
      </div>
      {tabState.hasPayment && (
        <View
          loading={userAccountsStore.loading}
          noData={!userAccountsStore.data.length}
          noDataText={t('noData.noRecords', { ns: 'common' })}
          error={userAccountsStore.error}
          onRefresh={() => $userAccountsStore.events.getUserAccounts({ AssignedUserId: props.user.id })}
        >
          <div className={cn('grid grid-cols-2 gap-4', 'max-lg:grid-cols-1', props.user.locked && 'pointer-events-none select-none opacity-40')}>
            {userAccountsStore.data.map((account) => (
              <AccountTileSmall key={account.id} account={account} />
            ))}
          </div>
        </View>
      )}
      <Cards
        title={
          tabState.hasPayment ? (
            <Text weight='medium' size='2xl'>
              {t('cards.title', { count: userCardsStore.data.data.length })}
            </Text>
          ) : undefined
        }
        userId={props.user.id}
      />
      <AddCardsModal
        isOpen={showAddCards}
        onClose={() => setShowAddCards(false)}
        user={props.user}
        accountsItems={userAccountsStore.data}
        cardsItems={userCardsStore.data.data}
        onSubmit={async (data) =>
          await Promise.all([
            tabState.hasPayment && data.accountIds.length > 0 && $userAccountsStore.events.addAccounts(data),
            data.cardIds.length > 0 && $userCardsStore.events.addCards(data),
          ])
        }
      />
      <RemoveCardsModal isOpen={showRemoveCards} user={props.user} onClose={() => setShowRemoveCards(false)} />
    </section>
  );
};
